import { useTranslation } from "react-i18next";

import type { ActId } from "../acts";
import { PHASE_BAR, missingMessage, phasePercents } from "../acts/investmentPhases";
import { formatBrl } from "../diagnosticData";
import { StackedBar } from "../primitives/StackedBar";
import { SummaryBlock, SummaryStat } from "./SummaryBlock";
import { SummaryLegend, Swatch } from "./SummaryLegend";
import { investmentFigures, monthsText } from "./summaryText";
import type { DiagnosticSummary } from "./summaryData";

/**
 * Bloco 4 · Investimento (EF-DIAG-004 RF-06): software, projeto, prazo e o
 * esforço por fase em %, como o Ato 4.
 *
 * Sem horas (EF-DIAG-003) e sem margem, fundings nem cenários (EF-FUNC-012): o
 * projeto é só o valor ofertado do cenário marcado. A barra de fases só existe
 * quando a calibragem publicada traz a partição; sem ela, o bloco diz o motivo
 * em vez de repartir o que sobrou.
 */
export function InvestmentBlock({
  summary,
  act,
  onGoToAct,
}: {
  summary: DiagnosticSummary["investment"];
  act: ActId;
  onGoToAct: (act: ActId) => void;
}) {
  const { t } = useTranslation();
  const figures = investmentFigures(summary, t);
  const { software, project, timeline } = summary;
  const phaseLabels: Record<string, string> = {
    explore_hh: t("Explore"),
    config_hh: t("Configure"),
    test_hh: t("Test"),
    deploy_hh: t("Deploy"),
    hypercare_hh: t("Hypercare"),
    overhead_hh: t("Overhead and contingency"),
  };
  const phases = timeline?.phases ?? undefined;
  const percents = phases ? phasePercents(phases) : [];
  const phasesMissing = timeline && !phases ? missingMessage(timeline.missing, t) : null;

  function reading(): string | undefined {
    const acv =
      software.state === "pricing_app" || software.state === "calculated" ? software.acv : undefined;
    const months = monthsText(timeline);
    if (acv === undefined || project.state !== "ready" || months === undefined) return undefined;
    return t("Software {{acv}} a year and a project of {{project}}, with go-live in {{months}} months.", {
      acv: formatBrl(acv),
      project: formatBrl(project.offered),
      months,
    });
  }

  return (
    <SummaryBlock
      id="investment"
      index={4}
      overline={t("Investment")}
      title={t("How much and how long")}
      act={act}
      onGoToAct={onGoToAct}
      reading={reading()}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <SummaryStat testId="summary-investment-software" compact {...figures.software} />
        <SummaryStat testId="summary-investment-project" compact {...figures.project} />
        <SummaryStat testId="summary-investment-timeline" compact {...figures.timeline} />
      </div>

      {phases && (
        <div className="flex flex-col gap-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
            {t("Effort by phase")}
          </div>
          <StackedBar
            height="h-3"
            segments={phases.map((phase, index) => ({
              key: phase.key,
              label: `% ${phaseLabels[phase.key]}`,
              className: PHASE_BAR[phase.key] ?? "bg-[#e8ebf0]",
              value: percents[index],
            }))}
          />
          <SummaryLegend
            testIdPrefix="summary-phase"
            items={phases.map((phase, index) => ({
              key: phase.key,
              label: phaseLabels[phase.key],
              value: `${percents[index]}%`,
              swatch: <Swatch className={PHASE_BAR[phase.key] ?? "bg-[#e8ebf0]"} />,
            }))}
          />
        </div>
      )}
      {phasesMissing && <p className="m-0 text-[11.5px] leading-snug text-[#7b8799]">{phasesMissing}</p>}
    </SummaryBlock>
  );
}
